/**
 * Cross-domain attribution capture.
 *
 * Captures UTM parameters and referrer on the very first page load
 * and persists them in sessionStorage. This lets us attribute every
 * login, signup intent, and Chargebee checkout to the original
 * traffic source.
 *
 * Keycloak uses `check-sso` (silent iframe), so unauthenticated/demo
 * users are never redirected — URL params stay intact. The full
 * redirect only happens when a user explicitly clicks "Login", but
 * by then captureAttribution() has already run at module level and
 * stored the params safely in sessionStorage.
 *
 * Domains involved:
 *   - newsquawk.com        (marketing / SEO landing pages)
 *   - squawkwatch.com      (retail content funnel)
 *   - app.newsquawk.com    (this app — demo + paid portal)
 */

const STORAGE_KEY = "nq_attribution";

export interface Attribution {
  utm_source?: string;
  utm_medium?: string;
  utm_campaign?: string;
  utm_content?: string;
  utm_term?: string;
  referrer?: string;
  /** The referring domain only (e.g. "newsquawk.com") */
  referrer_domain?: string;
  /** Landing page path on this app */
  landing_page?: string;
  /** ISO timestamp of first arrival */
  first_seen?: string;
}

/**
 * Call once on app mount. Reads UTM params from the URL and
 * document.referrer, then stores them in sessionStorage.
 *
 * Only captures on the *first* call per session — subsequent calls
 * (e.g. after Keycloak redirect) are no-ops so the original source
 * is preserved.
 */
export function captureAttribution(): void {
  try {
    // Already captured this session — don't overwrite
    if (sessionStorage.getItem(STORAGE_KEY)) return;

    const params = new URLSearchParams(window.location.search);

    const attribution: Attribution = {};

    // UTM parameters
    const utmSource = params.get("utm_source");
    const utmMedium = params.get("utm_medium");
    const utmCampaign = params.get("utm_campaign");
    const utmContent = params.get("utm_content");
    const utmTerm = params.get("utm_term");

    if (utmSource) attribution.utm_source = utmSource;
    if (utmMedium) attribution.utm_medium = utmMedium;
    if (utmCampaign) attribution.utm_campaign = utmCampaign;
    if (utmContent) attribution.utm_content = utmContent;
    if (utmTerm) attribution.utm_term = utmTerm;

    // Referrer — only capture external referrers (not our own domains)
    if (document.referrer) {
      try {
        const referrerUrl = new URL(document.referrer);
        const ownDomains = [
          "app.newsquawk.com",
          "auth.newsquawk.com",
          "auth.uat.newsquawk.com",
        ];
        if (!ownDomains.includes(referrerUrl.hostname)) {
          attribution.referrer = document.referrer;
          attribution.referrer_domain = referrerUrl.hostname;
        }
      } catch {
        // Malformed referrer — skip
      }
    }

    attribution.landing_page =
      window.location.pathname + window.location.search;
    attribution.first_seen = new Date().toISOString();

    // Only store if we captured at least one useful signal
    const hasSignal =
      attribution.utm_source ||
      attribution.utm_medium ||
      attribution.referrer_domain;

    if (hasSignal) {
      sessionStorage.setItem(STORAGE_KEY, JSON.stringify(attribution));
    }
  } catch {
    // sessionStorage unavailable (private browsing, etc.) — silently skip
  }
}

/**
 * Returns the stored attribution data, or null if none was captured.
 */
export function getAttribution(): Attribution | null {
  try {
    const raw = sessionStorage.getItem(STORAGE_KEY);
    return raw ? (JSON.parse(raw) as Attribution) : null;
  } catch {
    return null;
  }
}

/**
 * Build UTM query params from stored attribution for appending
 * to external URLs (e.g. Chargebee checkout).
 * Returns a string like "&utm_source=newsquawk&utm_medium=demo"
 * or empty string if no attribution exists.
 */
export function getAttributionQueryParams(): string {
  const attr = getAttribution();
  if (!attr) return "";

  const parts: string[] = [];
  if (attr.utm_source) parts.push(`utm_source=${encodeURIComponent(attr.utm_source)}`);
  if (attr.utm_medium) parts.push(`utm_medium=${encodeURIComponent(attr.utm_medium)}`);
  if (attr.utm_campaign) parts.push(`utm_campaign=${encodeURIComponent(attr.utm_campaign)}`);
  if (attr.utm_content) parts.push(`utm_content=${encodeURIComponent(attr.utm_content)}`);
  if (attr.utm_term) parts.push(`utm_term=${encodeURIComponent(attr.utm_term)}`);

  return parts.length > 0 ? "&" + parts.join("&") : "";
}
